/**
 * Application d'un plan de fusion (cf. `utils/branch-merge/plan.ts`) sur la base
 * ODA de la CIBLE, en lisant la base de la branche FUSIONNÉE. Les deux bases sont
 * des bases annexes (`openDatabaseFromBuffer`), jamais celle affichée : la fusion
 * ne touche pas au plan ouvert, le résultat est écrit puis envoyé comme révision.
 *
 * Ordre : éléments nommés (styles, calques, blocs), copie des entités, puis dans
 * l'ordre du plan suppressions, identités, opérations qui visent une copie
 * (rattachement, renumérotation), et présentations en dernier.
 *
 * Les copies passent par UN SEUL `wblockCloneObjects` : les dépendances partagées
 * (définitions de blocs, styles) ne sont clonées qu'une fois, celles qui existent
 * déjà dans la cible sont gardées (kDrcIgnore) — elles ont été mises à jour juste
 * avant si la fusion l'a décidé.
 *
 * Toute opération non appliquée est remontée à Sentry et listée dans le rapport.
 */

import { v4 as uuid } from "uuid";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  EntityRef,
  MergeOp,
  MergePlan,
  NamedRecordKind,
} from "@/utils/branch-merge/types";
import { deleteObj } from "../core/delete";
import { DuplicateRecordCloning, OpenMode } from "../core/constants";
import { findEntityByHandle, getEntityHandle, openAs } from "../core/oda";
import {
  readEntityMeta,
  writeEntityMeta,
  type EntityMeta,
} from "../meta/xdata";
import { getEntityType } from "../entities/detection";
import { savePhotomarkerLastNumber } from "../photomarker/xrecord";
import { writeMarkerPhotoIds } from "../photomarker/photo-link";
import { Logger } from "@/logger";

export interface ApplyMergeReport {
  /** Opérations qui n'ont pas pu être appliquées (entité introuvable, échec ODA). */
  failed: { op: string; detail: string }[];
}

function eraseEntity(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  handle: string,
): boolean {
  const id = findEntityByHandle(module, db, handle);
  if (!id) return false;
  try {
    const ent = openAs(id, module.OdDbEntity, OpenMode.kForWrite);
    ent.erase(true);
    deleteObj(ent);
    return true;
  } finally {
    deleteObj(id);
  }
}

/**
 * Remplace l'entité `handle` de la cible par la copie `copyId`, qui PREND SON
 * HANDLE (`swapIdWith`) : la fusion apparie les entités par handle, une copie
 * sous un nouveau handle ne serait plus retrouvée par les fusions suivantes.
 * Après l'échange, `copyId` désigne l'ancienne entité (supprimée) ; renvoie
 * l'id qui désigne désormais la copie, null si l'entité remplacée manque.
 */
function replaceKeepingHandle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  copyId: DrawingModule.OdDbObjectId,
  handle: string,
): DrawingModule.OdDbObjectId | null {
  const replacedId = findEntityByHandle(module, db, handle);
  if (!replacedId) return null;
  const copy = openAs(copyId, module.OdDbEntity, OpenMode.kForWrite);
  try {
    // XData et dictionnaire étendu restent avec leur contenu (DR_META de la copie).
    copy.swapIdWith(replacedId, false, false);
  } finally {
    deleteObj(copy);
  }
  const old = openAs(copyId, module.OdDbEntity, OpenMode.kForWrite);
  old.erase(true);
  deleteObj(old);
  return replacedId;
}

/** Met à jour le DR_META d'une entité, en créant une identité minimale au besoin. */
function updateMeta(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
  patch: Partial<EntityMeta>,
): void {
  const meta = readEntityMeta(module, id) ?? {
    entityUid: uuid(),
    kind: getEntityType(module, id),
    markerNumber: null,
    createdAt: null,
    defectName: null,
    background: true,
    observationId: null,
  };
  writeEntityMeta(module, id, { ...meta, ...patch });
}

type TableKind = Exclude<NamedRecordKind, "mleaderStyle" | "layout">;

function tableIdOf(
  db: DrawingModule.OdDbDatabase,
  kind: TableKind,
): DrawingModule.OdDbObjectId {
  switch (kind) {
    case "layer":
      return db.getLayerTableId();
    case "textStyle":
      return db.getTextStyleTableId();
    case "linetype":
      return db.getLinetypeTableId();
    case "dimStyle":
      return db.getDimStyleTableId();
    case "block":
      return db.getBlockTableId();
  }
}

/** Id de l'élément nommé `name` dans `db`, null s'il n'existe pas (à libérer). */
function findRecordId(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  kind: NamedRecordKind,
  name: string,
): DrawingModule.OdDbObjectId | null {
  const nameStr = new module.OdString(name);
  try {
    if (kind === "layout") {
      const id = db.findLayoutNamed(nameStr);
      if (!id.isNull()) return id;
      deleteObj(id);
      return null;
    }
    if (kind === "mleaderStyle") {
      const dictId = db.getMLeaderStyleDictionaryId(false);
      if (dictId.isNull()) {
        deleteObj(dictId);
        return null;
      }
      const dict = openAs(dictId, module.OdDbDictionary, OpenMode.kForRead);
      deleteObj(dictId);
      try {
        if (!dict.has1(nameStr)) return null;
        const obj = dict.getAt1(nameStr, OpenMode.kForRead);
        const id = obj.objectId();
        deleteObj(obj);
        return id;
      } finally {
        deleteObj(dict);
      }
    }
    const tableId = tableIdOf(db, kind);
    const table = openAs(tableId, module.OdDbSymbolTable, OpenMode.kForRead);
    deleteObj(tableId);
    try {
      return table.has1(nameStr) ? table.getAt3(nameStr, false) : null;
    } finally {
      deleteObj(table);
    }
  } finally {
    deleteObj(nameStr);
  }
}

/** Copie des objets de `mergedDb` sous `ownerId` (table, dictionnaire ou bloc de `targetDb`). */
function cloneInto(
  module: DrawingModuleType,
  mergedDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  sourceIds: DrawingModule.OdDbObjectId[],
  ownerId: DrawingModule.OdDbObjectId,
  cloning: DrawingModule.DuplicateRecordCloning,
): void {
  const ids = new module.OdDbObjectIdArray();
  for (const id of sourceIds) ids.push_back(id);
  const idMap = module.OdDbIdMapping.createObject();
  idMap.setDestDb(targetDb);
  try {
    mergedDb.wblockCloneObjects(ids, ownerId, idMap, cloning, false);
  } finally {
    deleteObj(ids);
    deleteObj(idMap);
  }
}

/** Ids des entités d'un bloc (espace papier compris), dans l'ordre. */
function blockEntityIds(
  module: DrawingModuleType,
  btrId: DrawingModule.OdDbObjectId,
): DrawingModule.OdDbObjectId[] {
  const ids: DrawingModule.OdDbObjectId[] = [];
  const btr = openAs(btrId, module.OdDbBlockTableRecord, OpenMode.kForRead);
  const iter = btr.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    ids.push(iter.objectId());
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(btr);
  return ids;
}

/**
 * Redéfinit le bloc `targetBtrId` d'après `sourceBtrId` : contenu remplacé, point
 * de base repris. `kDrcReplace` ne vaut pas pour une définition de bloc ; les
 * références existantes affichent le nouveau contenu (leurs attributs déjà
 * posés restent tels quels).
 */
function redefineBlock(
  module: DrawingModuleType,
  mergedDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  sourceBtrId: DrawingModule.OdDbObjectId,
  targetBtrId: DrawingModule.OdDbObjectId,
): void {
  for (const id of blockEntityIds(module, targetBtrId)) {
    try {
      const ent = openAs(id, module.OdDbEntity, OpenMode.kForWrite);
      ent.erase(true);
      deleteObj(ent);
    } finally {
      deleteObj(id);
    }
  }
  const sourceIds = blockEntityIds(module, sourceBtrId);
  try {
    if (sourceIds.length) {
      cloneInto(
        module,
        mergedDb,
        targetDb,
        sourceIds,
        targetBtrId,
        DuplicateRecordCloning.kDrcIgnore,
      );
    }
  } finally {
    for (const id of sourceIds) deleteObj(id);
  }
  const source = openAs(
    sourceBtrId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const origin = source.origin();
  deleteObj(source);
  const target = openAs(
    targetBtrId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  target.setOrigin(origin);
  deleteObj(target);
  deleteObj(origin);
}

/** Nombre d'entités de l'espace papier d'une présentation. */
function layoutEntityCount(
  module: DrawingModuleType,
  layoutId: DrawingModule.OdDbObjectId,
): number {
  const layout = openAs(layoutId, module.OdDbLayout, OpenMode.kForRead);
  const btrId = layout.getBlockTableRecordId();
  deleteObj(layout);
  const ids = blockEntityIds(module, btrId);
  for (const id of ids) deleteObj(id);
  deleteObj(btrId);
  return ids.length;
}

function deleteLayout(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  name: string,
): boolean {
  const id = findRecordId(module, db, "layout", name);
  if (!id) return false;
  deleteObj(id);
  const nameStr = new module.OdString(name);
  try {
    db.deleteLayout(nameStr);
    return true;
  } finally {
    deleteObj(nameStr);
  }
}

/**
 * Copie la présentation `name` de `mergedDb` dans `targetDb`, en remplaçant
 * celle du même nom. Par `cloneLayout`, comme la duplication d'une
 * présentation ; si la copie d'une base à l'autre échoue ou perd du contenu,
 * repli : présentation neuve + copie de l'espace papier (réglages d'impression
 * par défaut, signalé à Sentry).
 */
function copyLayout(
  module: DrawingModuleType,
  layoutManager: DrawingModule.OdDbLayoutManager,
  mergedDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  name: string,
): boolean {
  const sourceId = findRecordId(module, mergedDb, "layout", name);
  if (!sourceId) return false;
  const expected = layoutEntityCount(module, sourceId);
  deleteLayout(module, targetDb, name);

  const newNameStr = new module.OdString(name);
  const source = openAs(sourceId, module.OdDbLayout, OpenMode.kForRead);
  let cloneError: unknown = null;
  try {
    const clonedId = layoutManager.cloneLayout(
      targetDb,
      source,
      newNameStr,
      source.getTabOrder(),
    );
    try {
      if (
        clonedId.isNull() ||
        layoutEntityCount(module, clonedId) !== expected
      ) {
        cloneError = new Error(
          `copyLayout : cloneLayout a perdu du contenu (${name})`,
        );
      }
    } finally {
      deleteObj(clonedId);
    }
  } catch (e) {
    cloneError = e;
  } finally {
    deleteObj(source);
  }
  if (!cloneError) {
    deleteObj(newNameStr);
    deleteObj(sourceId);
    return true;
  }

  Logger.captureException(cloneError, {
    feature: "branch_merge",
    site: "apply.copyLayout",
    layout: name,
    fallback: true,
  });
  deleteLayout(module, targetDb, name);
  const emptyId = new module.OdDbObjectId();
  const layoutId = targetDb.createLayout(newNameStr, emptyId);
  deleteObj(emptyId);
  deleteObj(newNameStr);
  const layout = openAs(layoutId, module.OdDbLayout, OpenMode.kForRead);
  const btrId = layout.getBlockTableRecordId();
  deleteObj(layout);
  deleteObj(layoutId);

  const source2 = openAs(sourceId, module.OdDbLayout, OpenMode.kForRead);
  const sourceBtrId = source2.getBlockTableRecordId();
  deleteObj(source2);
  deleteObj(sourceId);
  const sourceIds = blockEntityIds(module, sourceBtrId);
  deleteObj(sourceBtrId);
  try {
    if (sourceIds.length) {
      cloneInto(
        module,
        mergedDb,
        targetDb,
        sourceIds,
        btrId,
        DuplicateRecordCloning.kDrcIgnore,
      );
    }
  } finally {
    for (const id of sourceIds) deleteObj(id);
    deleteObj(btrId);
  }
  return true;
}

/**
 * Reprend l'élément nommé `name` de la branche fusionnée. Styles, calques, types
 * de ligne : `kDrcReplace` (l'élément de la cible prend les propriétés décidées).
 * Bloc : copié s'il manque, redéfini sinon. Présentation : cf. `copyLayout`.
 */
function copyRecord(
  module: DrawingModuleType,
  layoutManager: DrawingModule.OdDbLayoutManager,
  mergedDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  kind: NamedRecordKind,
  name: string,
): boolean {
  if (kind === "layout") {
    return copyLayout(module, layoutManager, mergedDb, targetDb, name);
  }
  const sourceId = findRecordId(module, mergedDb, kind, name);
  if (!sourceId) return false;
  try {
    if (kind === "block") {
      const targetId = findRecordId(module, targetDb, kind, name);
      if (targetId) {
        try {
          redefineBlock(module, mergedDb, targetDb, sourceId, targetId);
        } finally {
          deleteObj(targetId);
        }
        return true;
      }
    }
    const ownerId =
      kind === "mleaderStyle"
        ? targetDb.getMLeaderStyleDictionaryId(true)
        : tableIdOf(targetDb, kind);
    try {
      cloneInto(
        module,
        mergedDb,
        targetDb,
        [sourceId],
        ownerId,
        DuplicateRecordCloning.kDrcReplace,
      );
    } finally {
      deleteObj(ownerId);
    }
    return true;
  } finally {
    deleteObj(sourceId);
  }
}

/** Copie les entités `mergedHandles` dans l'espace objet de la cible ; handle source → id copié. */
function cloneEntities(
  module: DrawingModuleType,
  mergedDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  mergedHandles: string[],
): Map<string, DrawingModule.OdDbObjectId> {
  const result = new Map<string, DrawingModule.OdDbObjectId>();
  if (!mergedHandles.length) return result;

  const sourceIds: DrawingModule.OdDbObjectId[] = [];
  const ids = new module.OdDbObjectIdArray();
  for (const h of mergedHandles) {
    const id = findEntityByHandle(module, mergedDb, h);
    if (id) {
      ids.push_back(id);
      sourceIds.push(id);
    }
  }
  const idMap = module.OdDbIdMapping.createObject();
  idMap.setDestDb(targetDb);
  const msId = targetDb.getModelSpaceId();
  try {
    mergedDb.wblockCloneObjects(
      ids,
      msId,
      idMap,
      DuplicateRecordCloning.kDrcIgnore,
      false,
    );
    // Même résolution que deepCloneEntities : une paire par source, jamais
    // l'itérateur de l'idMap (il rend aussi les dépendances clonées).
    for (const sourceId of sourceIds) {
      const pair = module.OdDbIdPair.ctr1(sourceId);
      try {
        if (idMap.compute(pair) && pair.isCloned()) {
          result.set(getEntityHandle(sourceId), pair.value());
        }
      } finally {
        deleteObj(pair);
      }
    }
  } finally {
    for (const id of sourceIds) deleteObj(id);
    deleteObj(ids);
    deleteObj(idMap);
    deleteObj(msId);
  }
  return result;
}

/** Handle du texte d'une ligne de repère, null si l'entité n'en est pas une ou n'en a pas. */
function leaderAnnotationHandle(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
): string | null {
  const ent = openAs(id, module.OdDbEntity, OpenMode.kForRead);
  try {
    const leader = module.OdDbLeader.cast(ent);
    if (!leader) return null;
    const annotationId = leader.annotationObjId();
    const handle = annotationId.isNull() ? null : getEntityHandle(annotationId);
    deleteObj(annotationId);
    deleteObj(leader);
    return handle;
  } finally {
    deleteObj(ent);
  }
}

/**
 * Une ligne de repère (`AcDbLeader`) copiée entraîne son texte, qu'elle référence
 * par un pointeur dur, même s'il n'est pas dans la liste des copies : il ferait
 * double avec celui de la cible. Le sort du texte reste celui décidé par le plan :
 * chaque copie est rattachée au texte du résultat (celui de la cible, ou sa propre
 * copie si elle a remplacé ce texte), ou détachée s'il a été supprimé ; le clone
 * entraîné est retiré.
 */
function relinkLeaderAnnotations(
  module: DrawingModuleType,
  targetDb: DrawingModule.OdDbDatabase,
  mergedDb: DrawingModule.OdDbDatabase,
  copied: Map<string, DrawingModule.OdDbObjectId>,
): void {
  for (const [mergedHandle, copyId] of copied) {
    const clonedHandle = leaderAnnotationHandle(module, copyId);
    if (!clonedHandle) continue;
    const sourceId = findEntityByHandle(module, mergedDb, mergedHandle);
    if (!sourceId) continue;
    const textHandle = leaderAnnotationHandle(module, sourceId);
    deleteObj(sourceId);
    if (!textHandle) continue;

    const copiedText = copied.get(textHandle);
    const targetText = copiedText
      ? null
      : findEntityByHandle(module, targetDb, textHandle);
    const textId = copiedText ?? targetText;
    try {
      if (textId && getEntityHandle(textId) === clonedHandle) continue;
      const ent = openAs(copyId, module.OdDbEntity, OpenMode.kForWrite);
      const leader = module.OdDbLeader.cast(ent);
      const clonedId = leader.annotationObjId();
      try {
        if (textId) leader.attachAnnotation(textId);
        else leader.detachAnnotation();
        if (!copiedText) {
          const clone = openAs(clonedId, module.OdDbEntity, OpenMode.kForWrite);
          clone.erase(true);
          deleteObj(clone);
        }
      } finally {
        deleteObj(clonedId);
        deleteObj(leader);
        deleteObj(ent);
      }
    } finally {
      if (targetText) deleteObj(targetText);
    }
  }
}

/**
 * Applique `plan` à `targetDb`. `lastMarkerNumber` : compteur de marqueurs photo à
 * persister dans le résultat (max des deux branches).
 */
export function applyMergePlan(
  module: DrawingModuleType,
  layoutManager: DrawingModule.OdDbLayoutManager,
  targetDb: DrawingModule.OdDbDatabase,
  mergedDb: DrawingModule.OdDbDatabase,
  plan: MergePlan,
  lastMarkerNumber: number,
): ApplyMergeReport {
  const failed: ApplyMergeReport["failed"] = [];
  const fail = (op: string, detail: string, e?: unknown) => {
    failed.push({ op, detail });
    Logger.captureException(
      e ?? new Error(`applyMergePlan : opération non appliquée (${op})`),
      { feature: "branch_merge", site: "apply", op, detail },
    );
  };

  // Styles, calques et blocs AVANT la copie des entités : les entités copiées
  // (kDrcIgnore) se rattachent par nom aux définitions déjà à jour.
  const isEarly = (op: MergeOp) =>
    op.op === "copyRecord" && op.kind !== "layout";

  let copied = new Map<string, DrawingModule.OdDbObjectId>();
  const resolve = (ref: EntityRef): DrawingModule.OdDbObjectId | null => {
    if (ref.side === "merged") return copied.get(ref.handle) ?? null;
    return findEntityByHandle(module, targetDb, ref.handle);
  };

  const run = (op: MergeOp) => {
    try {
      switch (op.op) {
        case "copyRecord":
          if (
            !copyRecord(
              module,
              layoutManager,
              mergedDb,
              targetDb,
              op.kind,
              op.name,
            )
          ) {
            fail(op.op, `${op.kind}:${op.name}`);
          }
          break;
        case "deleteLayout":
          if (!deleteLayout(module, targetDb, op.name)) fail(op.op, op.name);
          break;
        case "delete":
          if (!eraseEntity(module, targetDb, op.targetHandle)) {
            fail(op.op, op.targetHandle);
          }
          break;
        case "copy": {
          const id = copied.get(op.mergedHandle);
          if (!id) {
            fail(op.op, op.mergedHandle);
            break;
          }
          if (op.uid === "fresh") {
            if (readEntityMeta(module, id))
              updateMeta(module, id, { entityUid: uuid() });
          } else if (op.uid) {
            updateMeta(module, id, { entityUid: op.uid });
          }
          if (op.replaces) {
            const replacedId = replaceKeepingHandle(
              module,
              targetDb,
              id,
              op.replaces,
            );
            if (replacedId) {
              copied.set(op.mergedHandle, replacedId);
              deleteObj(id);
            } else fail("replace", op.replaces);
          }
          break;
        }
        case "setPhotos": {
          const id = resolve(op.ref);
          if (!id) {
            fail(op.op, op.ref.handle);
            break;
          }
          try {
            writeMarkerPhotoIds(module, id, op.photoIds);
          } finally {
            if (op.ref.side === "target") deleteObj(id);
          }
          break;
        }
        case "setUid":
        case "setObservation": {
          const id = resolve(op.ref);
          if (!id) {
            fail(op.op, op.ref.handle);
            break;
          }
          try {
            if (op.op === "setUid")
              updateMeta(module, id, { entityUid: op.uid });
            else if (op.observationId !== null || readEntityMeta(module, id)) {
              updateMeta(module, id, { observationId: op.observationId });
            }
          } finally {
            if (op.ref.side === "target") deleteObj(id);
          }
          break;
        }
      }
    } catch (e) {
      fail(op.op, "ref" in op ? op.ref.handle : JSON.stringify(op), e);
    }
  };

  for (const op of plan.ops) if (isEarly(op)) run(op);
  // Copies faites en une passe, avant de résoudre les références « merged ».
  try {
    copied = cloneEntities(
      module,
      mergedDb,
      targetDb,
      plan.ops.flatMap((o) => (o.op === "copy" ? [o.mergedHandle] : [])),
    );
  } catch (e) {
    fail("copy", "wblockCloneObjects", e);
  }
  for (const op of plan.ops) if (!isEarly(op)) run(op);
  // Après les remplacements : `copied` désigne alors les entités du résultat.
  try {
    relinkLeaderAnnotations(module, targetDb, mergedDb, copied);
  } catch (e) {
    fail("relinkLeaderAnnotations", "", e);
  }

  for (const id of copied.values()) deleteObj(id);
  savePhotomarkerLastNumber(module, targetDb, lastMarkerNumber);
  return { failed };
}

/** Écrit une base annexe en DWG (même format que la sauvegarde du plan). */
export function writeDatabaseToBuffer(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): Uint8Array {
  const FS = (
    window as unknown as {
      FS: { readFile(p: string): Uint8Array; unlink(p: string): void };
    }
  ).FS;
  const fileName = `merge_result_${Date.now()}.dwg`;
  const out = new module.OdString(fileName);
  try {
    db.writeFile1(out, 0, 33, false, 16);
  } finally {
    deleteObj(out);
  }
  const buf = FS.readFile(fileName);
  try {
    FS.unlink(fileName);
  } catch (e) {
    // Le résultat est lu : un fichier temporaire resté dans le FS WASM n'est
    // qu'une fuite mémoire, signalée.
    Logger.captureException(e, {
      feature: "branch_merge",
      site: "writeDatabaseToBuffer.unlink",
    });
  }
  if (!buf || buf.length === 0) {
    throw new Error("writeDatabaseToBuffer : ODA a produit un fichier vide");
  }
  return new Uint8Array(buf);
}
